import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../lib/api';
import {
  Upload, ImageIcon, X, Loader2, Stethoscope,
  User, FileText, AlertCircle, ChevronDown, ChevronUp,
  Scan, ShieldAlert, Plus, FileDigit, GitCompareArrows, MonitorUp
} from 'lucide-react';
import { ScreenCaptureDialog } from '../components/ScreenCaptureDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_IMAGES = 5;

const isDicomFile = (file) => file.name.toLowerCase().endsWith('.dcm') || file.type === 'application/dicom';

const UploadPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const priorInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [priorFile, setPriorFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [captureTarget, setCaptureTarget] = useState(null); // null | 'main' | 'prior'
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [showContext, setShowContext] = useState(true);
  const [showPrior, setShowPrior] = useState(false);

  // Clinical context form
  const [patientAge, setPatientAge] = useState('');
  const [patientSex, setPatientSex] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [traumaDetails, setTraumaDetails] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [reportLanguage, setReportLanguage] = useState(i18n.language || 'fr');

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type) && !isDicomFile(file)) {
      toast.error(t('errors.invalid_file'));
      return false;
    }
    if (file.size > MAX_SIZE) {
      toast.error(t('errors.file_too_large'));
      return false;
    }
    return true;
  };

  const makeEntry = (file) => ({
    file,
    isDicom: isDicomFile(file),
    previewUrl: isDicomFile(file) ? null : URL.createObjectURL(file),
    id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
  });

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList).filter(validateFile);
    setFiles((prev) => {
      const combined = [...prev, ...incoming.map(makeEntry)];
      if (combined.length > MAX_IMAGES) {
        toast.error(t('upload.max_images'));
        return combined.slice(0, MAX_IMAGES);
      }
      return combined;
    });
  };

  const removeFile = (id) => {
    setFiles((prev) => {
      const entry = prev.find((f) => f.id === id);
      if (entry?.previewUrl) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Paste image from clipboard (Ctrl+V)
  useEffect(() => {
    const onPaste = (e) => {
      if (isAnalyzing) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles = [];
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
            imageFiles.push(new File([file], `colle_${Date.now()}.${ext}`, { type: file.type }));
          }
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        addFiles(imageFiles);
        toast.success(t('upload.paste_added'));
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalyzing, t]);

  const handlePriorSelect = (file) => {
    if (!validateFile(file)) return;
    setPriorFile(makeEntry(file));
  };

  const handleCaptured = (file, analyzeNow = false) => {
    if (captureTarget === 'prior') {
      setPriorFile(makeEntry(file));
      setCaptureTarget(null);
      return;
    }
    const entry = makeEntry(file);
    const newFiles = [...files, entry].slice(0, MAX_IMAGES);
    setFiles(newFiles);
    setCaptureTarget(null);
    if (analyzeNow) {
      // Launch analysis immediately with the freshly captured image
      setTimeout(() => handleAnalyze(newFiles), 150);
    }
  };

  const handleAnalyze = async (filesOverride = null) => {
    const list = Array.isArray(filesOverride) && filesOverride.length ? filesOverride : files;
    if (list.length === 0) return;

    setIsAnalyzing(true);
    setAnalysisProgress(10);

    const formData = new FormData();
    list.forEach((entry) => formData.append('images', entry.file));
    if (priorFile) formData.append('prior_image', priorFile.file);
    formData.append('clinical_context', additionalContext);
    formData.append('patient_age', patientAge);
    formData.append('patient_sex', patientSex);
    formData.append('symptoms', symptoms);
    formData.append('trauma_details', traumaDetails);
    formData.append('language', reportLanguage);

    const progressInterval = setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev >= 85) {
          clearInterval(progressInterval);
          return 85;
        }
        return prev + Math.random() * 10;
      });
    }, 2000);

    try {
      const response = await api.post('/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });

      clearInterval(progressInterval);
      setAnalysisProgress(100);

      setTimeout(() => {
        navigate(`/analysis/${response.data.id}`);
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Analysis error:', error);
      const errorMsg = error.response?.data?.detail || t('errors.analysis_failed');
      toast.error(typeof errorMsg === 'string' ? errorMsg : t('errors.analysis_failed'));
      setIsAnalyzing(false);
      setAnalysisProgress(0);
    }
  };

  const FilePreview = ({ entry, onRemove, testId }) => (
    <div className="relative group rounded-lg overflow-hidden border border-[hsl(210,14%,18%)] bg-black">
      {entry.isDicom ? (
        <div className="h-28 flex flex-col items-center justify-center gap-1.5 bg-[hsl(210,18%,7%)]">
          <FileDigit size={24} className="text-teal-400" />
          <span className="text-[9px] font-bold text-teal-300 bg-teal-500/15 px-1.5 py-0.5 rounded">DICOM</span>
        </div>
      ) : (
        <img src={entry.previewUrl} alt={entry.file.name} className="h-28 w-full object-cover" />
      )}
      <div className="px-2 py-1 bg-[hsl(210,18%,7%)]">
        <p className="text-[9px] text-muted-foreground truncate">{entry.file.name}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500/80 text-white flex items-center justify-center transition-colors duration-150"
        data-testid={testId}
      >
        <X size={12} />
      </button>
    </div>
  );

  return (
    <div className="grid grid-cols-12 gap-4 md:gap-6">
      {/* Left column: Upload */}
      <div className="col-span-12 xl:col-span-7">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="bg-[hsl(210,18%,9%)] border-border">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Scan size={22} className="text-cyan-400" />
                {t('upload.title')}
              </CardTitle>
              <CardDescription className="text-xs">{t('upload.multi_hint')}</CardDescription>
            </CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <div
                  data-testid="image-upload-dropzone"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative flex flex-col items-center justify-center min-h-[280px] rounded-xl border-2 border-dashed cursor-pointer transition-colors duration-200 ${
                    isDragging
                      ? 'border-cyan-400 bg-cyan-500/5'
                      : 'border-[hsl(210,14%,22%)] hover:border-[hsl(210,14%,30%)] bg-[hsl(210,18%,7%)]'
                  }`}
                >
                  <div className="flex flex-col items-center gap-4 p-8">
                    <div className="w-16 h-16 rounded-2xl bg-[hsl(210,14%,14%)] flex items-center justify-center">
                      <Upload size={28} className="text-cyan-400" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">
                        {t('upload.dropzone')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('upload.dropzone_hint')}
                      </p>
                      <p className="text-xs text-cyan-300/80 mt-1.5" data-testid="paste-hint">
                        {t('upload.paste_hint')}
                      </p>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <p className="text-[10px] text-muted-foreground">{t('upload.supported')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('upload.max_size')} · {t('upload.max_images')}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="selected-images-grid">
                    {files.map((entry, i) => (
                      <FilePreview
                        key={entry.id}
                        entry={entry}
                        onRemove={() => removeFile(entry.id)}
                        testId={`remove-image-${i}`}
                      />
                    ))}
                    {files.length < MAX_IMAGES && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="h-28 rounded-lg border-2 border-dashed border-[hsl(210,14%,22%)] hover:border-cyan-400/50 flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:text-cyan-300 transition-colors duration-150"
                        data-testid="add-more-images-button"
                      >
                        <Plus size={20} />
                        <span className="text-[10px]">{t('upload.add_more')}</span>
                      </button>
                    )}
                    {files.length < MAX_IMAGES && (
                      <button
                        onClick={() => setCaptureTarget('main')}
                        className="h-28 rounded-lg border-2 border-dashed border-[hsl(210,14%,22%)] hover:border-cyan-400/50 flex flex-col items-center justify-center gap-1.5 text-muted-foreground hover:text-cyan-300 transition-colors duration-150"
                        data-testid="screen-capture-tile"
                      >
                        <MonitorUp size={20} />
                        <span className="text-[10px]">{t('capture.button_short')}</span>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ImageIcon size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {files.length} {t('upload.images_selected')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Screen capture button (empty state) */}
              {files.length === 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCaptureTarget('main')}
                  className="w-full mt-3 gap-2 text-xs border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10 h-10"
                  data-testid="screen-capture-button"
                >
                  <MonitorUp size={15} />
                  {t('capture.button')}
                </Button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,.dcm,application/dicom"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files);
                  e.target.value = '';
                }}
                data-testid="image-upload-input"
              />

              {/* Prior exam (comparison) */}
              <div className="mt-4 rounded-xl border border-[hsl(210,14%,18%)] bg-[hsl(210,18%,7%)]">
                <button
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  onClick={() => setShowPrior((v) => !v)}
                  data-testid="prior-exam-toggle"
                >
                  <span className="text-xs font-medium flex items-center gap-2 text-foreground/90">
                    <GitCompareArrows size={14} className="text-purple-400" />
                    {t('upload.prior_title')}
                  </span>
                  {showPrior ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {showPrior && (
                  <div className="px-4 pb-4">
                    <p className="text-[10px] text-muted-foreground mb-3">{t('upload.prior_hint')}</p>
                    {priorFile ? (
                      <div className="max-w-[160px]">
                        <FilePreview
                          entry={priorFile}
                          onRemove={() => setPriorFile(null)}
                          testId="remove-prior-image"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => priorInputRef.current?.click()}
                          className="text-xs gap-1.5 border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                          data-testid="add-prior-image-button"
                        >
                          <Plus size={13} />
                          {t('upload.prior_add')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCaptureTarget('prior')}
                          className="text-xs gap-1.5 border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                          data-testid="prior-capture-button"
                        >
                          <MonitorUp size={13} />
                          {t('capture.button_short')}
                        </Button>
                      </div>
                    )}
                    <input
                      ref={priorInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,.dcm,application/dicom"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) handlePriorSelect(e.target.files[0]);
                        e.target.value = '';
                      }}
                      data-testid="prior-image-input"
                    />
                  </div>
                )}
              </div>

              {/* Loading overlay */}
              <AnimatePresence>
                {isAnalyzing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="mt-6 p-6 rounded-xl bg-[hsl(210,18%,7%)] border border-border"
                  >
                    <div className="flex flex-col items-center gap-4">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 flex items-center justify-center">
                          <Loader2 size={28} className="text-cyan-400 animate-spin" />
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">{t('loading.analyzing')}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t('loading.analyzing_desc')}
                        </p>
                      </div>
                      <div className="w-full max-w-xs">
                        <Progress value={analysisProgress} className="h-2" />
                        <div className="flex justify-between mt-2">
                          <span className="text-[10px] text-muted-foreground">
                            {analysisProgress < 30 ? t('loading.step1') : analysisProgress < 70 ? t('loading.step2') : t('loading.step3')}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            {Math.round(analysisProgress)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Analyze Button */}
              {!isAnalyzing && (
                <div className="mt-6">
                  <Button
                    onClick={handleAnalyze}
                    disabled={files.length === 0 || isAnalyzing}
                    className="w-full h-12 text-base font-semibold bg-cyan-500 hover:bg-cyan-400 text-[hsl(210,25%,8%)] disabled:opacity-40"
                    data-testid="analyze-button"
                  >
                    <ShieldAlert size={18} className="mr-2" />
                    {files.length > 1 ? t('upload.analyze_multi', { count: files.length }) : t('upload.analyze')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Right column: Clinical Context */}
      <div className="col-span-12 xl:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-[hsl(210,18%,9%)] border-border">
            <CardHeader className="pb-3">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setShowContext(!showContext)}
              >
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Stethoscope size={18} className="text-teal-400" />
                    {t('context.title')}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    {t('context.subtitle')}
                  </CardDescription>
                </div>
                {showContext ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </CardHeader>

            {showContext && (
              <CardContent className="space-y-4" data-testid="clinical-context-form">
                {/* Report Language */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">{t('context.language')}</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={reportLanguage === 'fr' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setReportLanguage('fr')}
                      className={`flex-1 text-xs ${reportLanguage === 'fr' ? 'bg-cyan-500 text-[hsl(210,25%,8%)]' : ''}`}
                      data-testid="lang-fr-button"
                    >
                      {t('context.french')}
                    </Button>
                    <Button
                      variant={reportLanguage === 'en' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setReportLanguage('en')}
                      className={`flex-1 text-xs ${reportLanguage === 'en' ? 'bg-cyan-500 text-[hsl(210,25%,8%)]' : ''}`}
                      data-testid="lang-en-button"
                    >
                      {t('context.english')}
                    </Button>
                  </div>
                </div>

                <Separator className="bg-border" />

                {/* Patient info row */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="age" className="text-xs text-muted-foreground">
                      <User size={12} className="inline mr-1" />
                      {t('context.age')}
                    </Label>
                    <Input
                      id="age"
                      value={patientAge}
                      onChange={(e) => setPatientAge(e.target.value)}
                      placeholder={t('context.age_placeholder')}
                      className="h-9 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                      data-testid="patient-age-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('context.sex')}</Label>
                    <div className="flex gap-1">
                      {['M', 'F', 'X'].map((sex) => (
                        <Button
                          key={sex}
                          variant={patientSex === sex ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setPatientSex(sex === patientSex ? '' : sex)}
                          className={`flex-1 text-xs h-9 ${patientSex === sex ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' : ''}`}
                          data-testid={`sex-${sex.toLowerCase()}-button`}
                        >
                          {sex === 'M' ? t('context.sex_male') : sex === 'F' ? t('context.sex_female') : t('context.sex_other')}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Symptoms */}
                <div className="space-y-1.5">
                  <Label htmlFor="symptoms" className="text-xs text-muted-foreground">
                    <AlertCircle size={12} className="inline mr-1" />
                    {t('context.symptoms')}
                  </Label>
                  <Input
                    id="symptoms"
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                    placeholder={t('context.symptoms_placeholder')}
                    className="h-9 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                    data-testid="symptoms-input"
                  />
                </div>

                {/* Trauma */}
                <div className="space-y-1.5">
                  <Label htmlFor="trauma" className="text-xs text-muted-foreground">
                    <FileText size={12} className="inline mr-1" />
                    {t('context.trauma')}
                  </Label>
                  <Input
                    id="trauma"
                    value={traumaDetails}
                    onChange={(e) => setTraumaDetails(e.target.value)}
                    placeholder={t('context.trauma_placeholder')}
                    className="h-9 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                    data-testid="trauma-input"
                  />
                </div>

                {/* Additional Context */}
                <div className="space-y-1.5">
                  <Label htmlFor="context" className="text-xs text-muted-foreground">
                    {t('context.additional')}
                  </Label>
                  <textarea
                    id="context"
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder={t('context.additional_placeholder')}
                    rows={3}
                    className="flex min-h-[60px] w-full rounded-md border border-[hsl(210,14%,18%)] bg-[hsl(210,18%,7%)] px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    data-testid="additional-context-input"
                  />
                </div>
              </CardContent>
            )}
          </Card>

          {/* Urgency Legend */}
          <Card className="mt-4 bg-[hsl(210,18%,9%)] border-border">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-3 justify-center">
                {[
                  { level: 'CRITICAL', color: 'text-red-400 bg-red-500/15 ring-red-500/35', icon: '\u25B2' },
                  { level: 'HIGH', color: 'text-orange-400 bg-orange-500/15 ring-orange-500/35', icon: '\u26A1' },
                  { level: 'MODERATE', color: 'text-yellow-400 bg-yellow-500/15 ring-yellow-500/35', icon: '\u25CF' },
                  { level: 'LOW', color: 'text-green-400 bg-green-500/15 ring-green-500/35', icon: '\u2713' },
                ].map(({ level, color, icon }) => (
                  <span
                    key={level}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ring-1 ${color}`}
                  >
                    <span>{icon}</span>
                    {level}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Screen capture dialog */}
      <ScreenCaptureDialog
        open={!!captureTarget}
        onOpenChange={(o) => !o && setCaptureTarget(null)}
        onCapture={handleCaptured}
        allowAnalyzeNow={captureTarget === 'main'}
        t={t}
      />
    </div>
  );
};

export default UploadPage;
